import React, { useState, useRef, useEffect, useCallback } from "react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import { useLang } from "../../i18n";
import { PhaseIntro, RevisitCallout } from "../../components/phase-common";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Check, Loader2 } from "lucide-react";

const FIELDS_NL = [
  { k: "name", label: "Naam organisatie", type: "input" },
  { k: "sector", label: "Branche / sector", type: "input" },
  { k: "products", label: "Producten / diensten", type: "textarea" },
  { k: "problems", label: "Welk probleem of welke problemen lost de organisatie op?", type: "textarea" },
  { k: "audience", label: "Doelgroep(en)", type: "textarea" },
  { k: "mission", label: "Missie", type: "textarea" },
  { k: "vision", label: "Visie", type: "textarea" },
  { k: "values", label: "Kernwaarden", type: "textarea" },
  { k: "usp", label: "Wat maakt jullie uniek?", type: "textarea" },
  { k: "tone", label: "Toon / stijl van communicatie", type: "input" },
  { k: "history", label: "Korte geschiedenis / oorsprong", type: "textarea" },
];
const FIELDS_EN = [
  { k: "name", label: "Organization name", type: "input" },
  { k: "sector", label: "Industry / sector", type: "input" },
  { k: "products", label: "Products / services", type: "textarea" },
  { k: "problems", label: "Which problem(s) does the organization solve?", type: "textarea" },
  { k: "audience", label: "Target audience(s)", type: "textarea" },
  { k: "mission", label: "Mission", type: "textarea" },
  { k: "vision", label: "Vision", type: "textarea" },
  { k: "values", label: "Core values", type: "textarea" },
  { k: "usp", label: "What makes you unique?", type: "textarea" },
  { k: "tone", label: "Tone / communication style", type: "input" },
  { k: "history", label: "Brief history / origin", type: "textarea" },
];

export default function Phase1({ org, refresh }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(org.phase1 || {});
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const timer = useRef(null);
  const skipFirst = useRef(true);
  const fields = lang === "nl" ? FIELDS_NL : FIELDS_EN;

  const persist = useCallback(async (payload) => {
    setStatus("saving");
    try {
      await api.patch("/org/phase1", { data: payload });
      setStatus("saved");
      refresh();
    } catch (e) {
      setStatus("idle");
      toast.error(apiErr(e.response?.data?.detail));
    }
  }, [refresh]);

  useEffect(() => {
    if (skipFirst.current) { skipFirst.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    setStatus("saving");
    timer.current = setTimeout(() => persist(data), 900);
    return () => timer.current && clearTimeout(timer.current);
  }, [data, persist]);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <PhaseIntro phase={1} title={t("p1_title")} intro={t("p1_intro")} />
      <RevisitCallout coreEmotion={org.core_emotion} subject={t("p1_subject")} />

      {status !== "idle" && (
        <div
          data-testid="p1-autosave-status"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full border border-border bg-card/95 px-4 py-2.5 shadow-xl shadow-primary/10 backdrop-blur transition-all"
        >
          {status === "saving" ? (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {lang === "nl" ? "Opslaan…" : "Saving…"}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
              <Check className="h-4 w-4" /> {lang === "nl" ? "Automatisch opgeslagen" : "Saved automatically"}
            </span>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.k} className={`space-y-1.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
              <Label>{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`p1-${f.k}`} value={data[f.k] || ""} onChange={set(f.k)} rows={3} />
              ) : (
                <Input data-testid={`p1-${f.k}`} value={data[f.k] || ""} onChange={set(f.k)} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
